/**
 * PsbFixturePage — Preview-only UAT fixture for GoverNedNewProductModal
 *
 * Mounts the production GoverNedNewProductModal component with a synthetic
 * LIMONCELLO-equivalent row and AI prefill (category="Liqueurs", pack_ml=700,
 * bottles_per_case=12, uk_selling_measure_ml=25).
 *
 * Purpose: Wave 1 Phase B acceptance evidence — demonstrate autoApply and
 * manual Apply flows against the actual modal/effects, not a lifecycle harness.
 *
 * UAT steps:
 *   AUTO  — click "Open — Auto Apply (AI 1-click path)" → Category=Spirits,
 *            Subcategory=Liqueurs appear WITHOUT clicking Apply → Cancel
 *   MANUAL — click "Open — Manual Apply" → form starts empty → click
 *            "Apply AI suggestions" → Category=Spirits, Subcategory=Liqueurs
 *            appear → Cancel
 *
 * Structural isolation (three independent layers):
 *   1. submitAdapter={fixtureNoWriteAdapter} intercepts the ONLY write path
 *      (the Save / create-product call) at the JS call-site inside the modal.
 *      The adapter returns a synthetic object; axios.post is NEVER invoked.
 *      This makes the fixture structurally unable to mutate any product data,
 *      regardless of whether mc_id 999501 exists in the database or not.
 *   2. Fail-closed hostname allowlist (line ~41): only localhost, 127.0.0.1,
 *      *.emergent.host, *.preview.*, *.emergentagent.com are allowed.  Any
 *      other host (including www.abbeyzone.co.uk) renders a locked screen.
 *      Replaces denylist — fails CLOSED for all unrecognised hostnames.
 *   3. Backend PSB write gate: _psb_writes_allowed() checks APP_ENV !== 'production'
 *      AND UAT_FIXTURE_ENABLED === '1'. Calls from production are rejected
 *      server-side even if the frontend gate were somehow bypassed.
 *
 * Read-only API calls (taxonomy lookups, measures) are allowed — they do not
 * write to any table. aiPrefill is injected as static fixture data so no live
 * AI API call is made for the fixture row.
 *
 * Route: /stocktaking/psb-stage1b-fixture   (AdminRoute protected, Preview only)
 */
import React, { useState, useEffect } from "react";
import { GoverNedNewProductModal } from "./PsbStage1BPage";
import { Button } from "../components/ui/button";

// ── Production gate — EXACT-MATCH ALLOWLIST ──────────────────────────────────
// Fail-CLOSED: only the three explicitly approved hostnames are allowed.
// A Set membership check (not string.includes) is used so a host like
// evil.preview.example.com is NOT matched even though it contains '.preview.'.
// Any hostname absent from this Set renders the locked screen.
//
// Add new Preview hostnames here explicitly; remove stale ones on rotation.
// SSR / build-time fallback uses the explicit opt-in env flag.
const PREVIEW_HOST_ALLOWLIST = new Set([
  'localhost',
  '127.0.0.1',
  'audit-variance-qa.preview.emergentagent.com',    // previous job's Preview
  'stock-audit-preview-1--1euof15n.preview.emergentagent.com',  // current live Preview — added 2026-11-03
]);
const isPreviewOrDev =
  typeof window !== 'undefined'
    ? PREVIEW_HOST_ALLOWLIST.has(window.location.hostname)
    : process.env.REACT_APP_ENABLE_PSB_FIXTURE === 'true';

// ── Synthetic LIMONCELLO-equivalent fixture data ───────────────────────────────
const FIXTURE_ROW = {
  mc_id:              999501,
  itemcode:           "999501",
  source_description: "LIMONCELLO PALLINI",
  group_code:         "10",
  plu_value_inc_vat:  0,
  s4_plu_revenue:     0,
};

const FIXTURE_AIPREFILL = {
  enabled: true,
  prefill: {
    suggested_name:           "Limoncello Pallini",
    brand:                    "Pallini",
    // AI returns the subcategory name as the top-level category — the Wave 1 bug scenario
    category:                 "Liqueurs",
    subcategory:              null,
    pack_ml:                  700,
    pack_type:                "bottle",
    pack_label:               "bottle",
    bottles_per_case:         12,
    uk_selling_measure_ml:    25,
    uk_selling_measure_label: "shot",
    confidence:               { category: "high", pack: "high" },
  },
  citations: ["Preview fixture — not from AI API"],
};

// ── MINI ROSE PROSECCO equivalent — taxonomy token-flip + unit-snap fixture ──
// Mirrors the HI Gloucester 176000 scenario: AI returns "Champagne/Sparkling"
// as the category (reversed token order vs catalog "Sparkling / Champagne"),
// with no citations. Proves Pass 2 token fix and purchase/sell unit snapping.
const FIXTURE_ROW_PROSECCO = {
  mc_id:              999502,
  itemcode:           "999502",
  source_description: "MINI ROSE PROSECCO 200ML",
  group_code:         "17",
  plu_value_inc_vat:  0,
  s4_plu_revenue:     0,
};
const FIXTURE_AIPREFILL_PROSECCO = {
  enabled: true,
  prefill: {
    suggested_name:           "Mini Rosé Prosecco 200ml",
    brand:                    null,
    category:                 "Champagne/Sparkling",   // reversed token order — taxonomy snap test
    subcategory:              "Prosecco Rosé",          // not in catalog — should not block sub resolution
    pack_ml:                  200,
    pack_type:                "bottle",
    pack_label:               "bottle",                 // generic label — unit-snap test
    bottles_per_case:         null,
    uk_selling_measure_ml:    125,
    uk_selling_measure_label: "125ml glass",            // disambiguation hint for wine_125ml
    confidence:               { category: "high", pack: "medium" },
  },
  citations: [],   // no citations — snap must work without them
};

// ── GUINNESS 0% equivalent — alcohol-free parent family + unit-snap fixture ──
// Mirrors HI Gloucester 020600: AI correctly resolves Beer/Bottled Beer & Cider
// but pack_label="can" is not in the org's named-measure catalog (only "Can 558ml").
const FIXTURE_ROW_GUINNESS_AF = {
  mc_id:              999503,
  itemcode:           "999503",
  source_description: "GUINNESS 0% 558ML CAN",
  group_code:         "02",
  plu_value_inc_vat:  0,
  s4_plu_revenue:     0,
};
const FIXTURE_AIPREFILL_GUINNESS_AF = {
  enabled: true,
  prefill: {
    suggested_name:           "Guinness 0% 558ml",
    brand:                    "Guinness",
    category:                 "Bottled Beer & Cider",   // AI names subcategory as category
    subcategory:              "Alcohol-Free Beer",       // not in catalog → Pass 3 "beer" token fallback
    pack_ml:                  558,
    pack_type:                "can",
    pack_label:               "can",                    // generic → should snap to can_558ml
    bottles_per_case:         24,
    uk_selling_measure_ml:    558,
    uk_selling_measure_label: "Can (558ml)",
    is_alcoholic:             true,
    confidence:               { category: "high", pack: "high" },
  },
  citations: [],   // no citations
};

// ── TWO FARMERS CRISPS equivalent — packaged-snack Packet/Case pattern ──────
// 2026-11 fixture addition: exercises the governed-create form for the
// Stage 1A packaged-snack count pattern (Packet = each, Case = N-per-case).
// NOTE: the "Apply AI suggestions" pipeline above (bottleMl/caseTotalMl
// snapping, standard-bottle-ml rounding, etc.) is built entirely around
// VOLUME units and has no count-pattern branch — so this fixture's
// aiPrefill is deliberately `enabled: false` with an empty `prefill`.
// The modal opens with a BLANK form (just the product name seeded from
// the synthetic row) and the tester manually adds the Packet ("each",
// qty 1) and Case ("each", qty e.g. 12) unit rows through the same
// generic measure-row UI used for every other product — this is an
// honest fixture of what the shared form actually supports today for
// snacks, not a simulation of AI auto-apply logic that doesn't exist yet.
const FIXTURE_ROW_SNACK = {
  mc_id:              999504,
  itemcode:           "999504",
  source_description: "TWO FARMERS LIGHTLY SEA SALTED CRISPS 40G",
  group_code:         "25",
  plu_value_inc_vat:  0,
  s4_plu_revenue:     0,
};
const FIXTURE_AIPREFILL_SNACK = {
  enabled: false,
  prefill: {},
  citations: [],
};
// Intercepts every Save in the fixture before any axios call is made.
// Returns a synthetic response so onSuccess can log it without writing anything.
const fixtureNoWriteAdapter = async (body) => ({
  _fixture:     true,
  mc_id:        body.mc_id,
  proposed_name: body.proposed_name,
});

export default function PsbFixturePage() {
  const [mode, setMode]       = useState(null);  // null | "auto" | "manual"
  const [fixture, setFixture] = useState("limoncello"); // which fixture row is active
  const [lastResult, setLastResult] = useState(null);

  // Production gate — render a locked page outside Preview / dev.
  // Hooks must be declared before any conditional return (Rules of Hooks).

  if (!isPreviewOrDev) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-500" data-testid="fixture-unavailable">
          This page is not available in this environment.
        </p>
      </div>
    );
  }

  // The modal makes two read-only API calls (taxonomy categories, measure
  // definitions).  Both use freshToken from localStorage internally — they
  // do NOT require an s1RunId.  s1RunId is used only in the write path
  // (create-product POST), which fixtureNoWriteAdapter short-circuits before
  // it can be reached.  No persisted PSB session is therefore needed.
  const headers = { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` };

  const handleCancel = () => {
    setLastResult("cancelled — no write");
    setMode(null);
  };

  const handleSuccess = (data) => {
    // submitAdapter returns { _fixture: true, ... } — nothing was written
    setLastResult(`fixture-save (no write): ${JSON.stringify(data).slice(0, 80)}`);
    setMode(null);
  };

  // Derive active row / prefill from fixture selection
  const activeRow      = fixture === "prosecco"    ? FIXTURE_ROW_PROSECCO
                       : fixture === "guinness_af" ? FIXTURE_ROW_GUINNESS_AF
                       : fixture === "snack"        ? FIXTURE_ROW_SNACK
                       : FIXTURE_ROW;
  const activePrefill  = fixture === "prosecco"    ? FIXTURE_AIPREFILL_PROSECCO
                       : fixture === "guinness_af" ? FIXTURE_AIPREFILL_GUINNESS_AF
                       : fixture === "snack"        ? FIXTURE_AIPREFILL_SNACK
                       : FIXTURE_AIPREFILL;

  const FIXTURE_META = {
    limoncello: {
      label: "LIMONCELLO PALLINI (mc_id 999501)",
      expected: "Category=Spirits, Subcategory=Liqueurs; units: Bottle 700ml · Shot 25ml. NO Case row for spirits.",
    },
    prosecco: {
      label: "MINI ROSÉ PROSECCO 200ML (mc_id 999502) — taxonomy token-flip + unit snap",
      expected: "Category=Wine, Subcategory=Sparkling / Champagne; units: Bottle 200ml (purchase) · Wine 125ml (selling). No citations — snap must fire from ml match alone.",
    },
    guinness_af: {
      label: "GUINNESS 0% 558ML CAN (mc_id 999503) — alcohol-free parent family + unit snap",
      expected: "Category=Beer, Subcategory=Bottled Beer & Cider; unit: Can 558ml (purchase+selling merged). No Case row (citations=[] blocks bpc logic).",
    },
    snack: {
      label: "TWO FARMERS CRISPS 40G (mc_id 999504) — packaged-snack Packet/Case pattern (manual, no AI-apply)",
      expected: "Form opens BLANK (AI-apply has no count-unit branch yet). Manually add unit 'each / unit', qty 1, tick Default Count + Default Purchase — this is the Packet row. Add a second 'each / unit' row, qty 12, for the Case. Save must succeed with no Post-Mix toggle available on this form.",
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <p className="text-sm font-semibold text-amber-800">
            PSB AI SNAP — UAT FIXTURE (Preview only · no write)
          </p>
          <p className="text-xs text-amber-700 mt-1">
            Mounts the production <code>GoverNedNewProductModal</code> with synthetic rows.
            Real taxonomy / measures APIs are called. <strong>Save is intercepted — no product is ever created.</strong>
          </p>
        </div>

        {/* Fixture selector */}
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-700">Select fixture row</p>
          <div className="flex flex-col gap-2">
            {Object.entries(FIXTURE_META).map(([key, meta]) => (
              <label key={key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${fixture === key ? 'bg-violet-50 border-violet-300' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="fixture"
                  value={key}
                  checked={fixture === key}
                  onChange={() => { setFixture(key); setMode(null); setLastResult(null); }}
                  className="mt-0.5"
                  data-testid={`fixture-select-${key}`}
                />
                <div>
                  <p className="text-xs font-medium text-gray-800">{meta.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5"><span className="font-medium">Expected:</span> {meta.expected}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* UAT Controls */}
        <div className="bg-white border rounded-lg p-4 space-y-4">
          <p className="text-sm font-semibold text-gray-700">UAT Steps</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="fixture-open-auto-btn"
              variant="default"
              className="flex-1"
              onClick={() => { setLastResult(null); setMode("auto"); }}
            >
              1. Open — Auto Apply (AI 1-click path)
            </Button>
            <Button
              data-testid="fixture-open-manual-btn"
              variant="outline"
              className="flex-1"
              onClick={() => { setLastResult(null); setMode("manual"); }}
            >
              2. Open — Manual Apply
            </Button>
          </div>

          <ol className="text-xs text-gray-600 list-decimal list-inside space-y-1 bg-gray-50 rounded p-3">
            <li><strong>AI Apply:</strong> Expected Category/Subcategory and unit types must appear as shown above for the selected fixture.</li>
            <li><strong>Manual override:</strong> After Apply, change a unit type or category — the edit must persist. Apply again must NOT overwrite a valid catalog value you set.</li>
            <li><strong>Cancel = no write:</strong> Cancel the modal. Result below must show "cancelled — no write". No product row created.</li>
            <li><strong>Explicit Create + reload:</strong> Click Save (fixture-intercepted). Result shows fixture-save. Reopen the fixture — form starts fresh (no leftover data from previous open).</li>
            <li><strong>No duplicate measure rows:</strong> Apply multiple times — units section must not accumulate extra rows.</li>
            <li><strong>Product-code range:</strong> The product-code field is read-only — the system auto-assigns the next code in the subcategory range on save. No manual code entry is accepted. Saving a product must succeed and show the allocated code in the fixture result.</li>
          </ol>

          {lastResult && (
            <p
              data-testid="fixture-last-result"
              className={`text-xs rounded p-2 ${lastResult.startsWith("cancelled") ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}
            >
              Result: <strong>{lastResult}</strong>
            </p>
          )}
        </div>

      </div>

      {/* Real production modal — submitAdapter intercepts the only write path. */}
      {mode !== null && (
        <GoverNedNewProductModal
          key={`${fixture}-${mode}`}        /* remount cleanly when switching modes or fixtures */
          row={activeRow}
          s1RunId="synthetic-fixture-run-999"
          siteId={9999}
          headers={headers}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          aiPrefill={activePrefill}
          aiPrefillLoading={false}
          autoApplyAI={mode === "auto"}
          submitAdapter={fixtureNoWriteAdapter}
        />
      )}
    </div>
  );
}
